import { AudioManager } from "../../AudioManager";
import { GameConstants } from "../../GameConstants";
import { GameVars } from "../../GameVars";
import { GUI } from "./GUI";
import { GUIDevelopment } from "./GUIDevelopment";
import { HUD } from "./hud/HUD";
import { PauseLayer } from "./PauseLayer";
import { BoardContainer } from "./BoardContainer";
import { BoardManager } from "./BoardManager";
import { Orb } from "./orbs/Orb";
import { GameOverLayer } from "./GameOverLayer";
import ICandyScene from "../ICandyScene";
import { GameManager } from "../../GameManager";
import * as Candy from "@ravalmatic/candy-engine";

export class BoardScene extends Phaser.Scene implements ICandyScene {

    public static currentInstance: BoardScene;

    public boardManager: BoardManager;

    private hud: HUD;
    private gui: GUI;
    private guiDevelopment: GUIDevelopment;
    private boardContainer: BoardContainer;
    private pauseLayer: PauseLayer;
    private gameOverLayer: GameOverLayer;
    
    constructor() {

        super("BoardScene");

        BoardScene.currentInstance = this;
    }

    public create(): void {

        GameManager.setCurrentScene(this);

        this.boardManager = new BoardManager(this);

        const background = this.add.image(GameConstants.GAME_WIDTH / 2, GameConstants.GAME_HEIGHT / 2, "background");
        background.setScale(2);

        this.boardContainer = new BoardContainer(this);
        this.add.existing(this.boardContainer);

        this.hud = new HUD(this);
        this.add.existing(this.hud);

        if (GameConstants.DEBUG_MODE) {
            this.guiDevelopment = new GUIDevelopment(this);
            this.add.existing(this.guiDevelopment);
        }

        this.gui = new GUI(this);
        this.add.existing(this.gui);

        this.input.on("pointerdown", this.onPointerDown, this);

        GameVars.paused = false;

        AudioManager.playSound("soundtrack", true, .5);
    }

    public update(): void {

        this.boardContainer.update();
        this.hud.update();

        if (!GameVars.orbsMoving && !GameVars.levelingUp && !GameVars.paused && !GameVars.gameOver && this.input.activePointer.isDown) {

            const p1 = this.boardManager.p1;
            const p2 = this.boardManager.p2;

            if (p1 && p2 === null) {

                const p2 = this.getOrbPosition();

                if (p2 && !(p1.c === p2.c && p1.r === p2.r)) {
                    this.boardManager.onOrbSelected(p2);
                }
            }
        }
    }

    public pause(): void {
        
        this.pauseLayer = new PauseLayer(this);
        this.add.existing(this.pauseLayer);
    }

    public resume(): void {

        this.pauseLayer.destroy();
    }

    public onLevelUp(firstInstantiation?: boolean, newCol?: boolean): void {

        this.hud.onLevelUp();

        this.time.delayedCall(350, function(): void {
            this.boardContainer.onLevelUp(false, newCol);
        }, [], this);   
    }

    public updateMoves(): void {

        this.hud.updateMoves();
    }

    public updateScore(orbsEliminated: number, rematchesCounter: number): void {

        this.hud.updateScore(orbsEliminated, rematchesCounter);
    }

    public onNoRematch(orbsEliminated: number): void {
        
        this.hud.updateScore(orbsEliminated);
    }

    public onGameOver(): void {

        this.hud.onGameOver();

        this.gameOverLayer = new GameOverLayer(this);
        this.add.existing(this.gameOverLayer);

        AudioManager.stopLoop();
        AudioManager.playSound("gameover", false, .75);
        this.time.delayedCall(1000, function(): void {
            AudioManager.playSound("gameover_loop", true);
        }, [], this);   
    }

    public reshuffle(prevBoard: Candy.OrbData[][]): void {

        this.hud.onReshuffle();
        this.boardContainer.reshuffle(prevBoard);
    }

    private onPointerDown(): void {

        if (GameVars.paused || GameVars.orbsMoving || GameVars.levelingUp || GameVars.gameOver) {
            return;
        }

        const p = this.getOrbPosition();

        if (p) {
            this.boardManager.onOrbSelected(p);
        }
    }

    private getOrbPosition(): {c: number, r: number} {

        const x = (this.input.activePointer.x - this.boardContainer.x) / this.boardContainer.scaleX + Orb.SIZE / 2;
        const y = (this.input.activePointer.y - this.boardContainer.y) / this.boardContainer.scaleY + Orb.SIZE / 2;

        const c = Math.floor(x / Orb.SIZE);
        const r = Math.floor(y / Orb.SIZE);

        const cols = GameVars.cols;
        const rows = GameVars.rows;

        if (c >= 0 && c < cols && r >= 0 && r < rows) {
            return {c: c, r: r};
        } else {
            return null;
        }
    }
}
